// src/pages/estoque/PesqEstoquePage.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { useMessage } from '../../contexts/geral/MessageContext';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { formatCurrency, formatNumeric, parseNumeric } from '../../utils/numberUtils';
import { buscarEstoques } from '../../services/estoque/EstoqueService';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import Input from '../../components/input/Input';
import ModalEstoqueMov from './ModalEstoqueMov';

const normalizeText = (text) => {
    return text
        ? text
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .toLowerCase()
            .trim()
        : '';
};

const PesqEstoquePage = () => {
    const { showMessage } = useMessage();
    const [estoques, setEstoques] = useState([]);
    const [produtoId, setProdutoId] = useState('');
    const [descricao, setDescricao] = useState('');
    const [somenteAbaixoMin, setSomenteAbaixoMin] = useState(false);
    const [disableBtn, setDisableBtn] = useState(false);
    const [showMovModal, setShowMovModal] = useState(false);
    const [produtoMovSel, setProdutoMovSel] = useState(null);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });

    useEffect(() => {
        buscarEstoque();
    }, []);

    const buscarEstoque = async () => {
        try {
            setDisableBtn(true);
            const result = await buscarEstoques();
            setEstoques(Array.isArray(result) ? result : []);
        } catch (error) {
            showMessage(`Erro ao buscar estoque: ${error?.message || error}`, 'danger');
        } finally {
            setDisableBtn(false);
        }
    };

    const estoquesFiltrados = useMemo(() => {
        return estoques.filter((registro) => {
            const idMatch = produtoId
                ? registro.produtoId?.toString() === produtoId.toString().trim()
                : true;

            const descricaoMatch = descricao
                ? normalizeText(registro.produto?.descricao || '').includes(normalizeText(descricao))
                : true;

            const estoqueMin = parseNumeric(registro.produto?.estoqueMin);
            const quantidade = parseNumeric(registro.quantidade);
            const abaixoDoMinimo = estoqueMin > 0 && quantidade < estoqueMin;

            const minimoMatch = somenteAbaixoMin ? abaixoDoMinimo : true;

            return idMatch && descricaoMatch && minimoMatch;
        });
    }, [estoques, produtoId, descricao, somenteAbaixoMin]);

    const totalQuantidade = useMemo(() => {
        return estoquesFiltrados.reduce((acc, item) => acc + parseNumeric(item.quantidade), 0);
    }, [estoquesFiltrados]);

    const getStatusEstoque = (item) => {
        const estoqueMin = parseNumeric(item.produto?.estoqueMin);
        const quantidade = parseNumeric(item.quantidade);

        if (estoqueMin <= 0) return 'Sem mínimo';
        if (quantidade < estoqueMin) return 'Abaixo do mínimo';
        return 'Normal';
    };

    const estoquesOrdenados = useMemo(() => {
        const { campo, direcao } = ordenacao;

        if (!campo) {
            return estoquesFiltrados;
        }

        const multiplicador = direcao === 'asc' ? 1 : -1;

        return [...estoquesFiltrados].sort((a, b) => {
            const valorA = campo === 'descricao'
                ? a.produto?.descricao
                : campo === 'estoqueMin'
                    ? a.produto?.estoqueMin
                    : campo === 'status'
                        ? getStatusEstoque(a)
                        : campo === 'preco'
                            ? a.produto?.preco
                            : a?.[campo];
            const valorB = campo === 'descricao'
                ? b.produto?.descricao
                : campo === 'estoqueMin'
                    ? b.produto?.estoqueMin
                    : campo === 'status'
                        ? getStatusEstoque(b)
                        : campo === 'preco'
                            ? b.produto?.preco
                            : b?.[campo];

            if (['id', 'produtoId', 'quantidade', 'estoqueMin', 'preco'].includes(campo)) {
                return (parseNumeric(valorA) - parseNumeric(valorB)) * multiplicador;
            }

            if (campo === 'dataUltMov') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }

            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [estoquesFiltrados, ordenacao]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo
                ? (atual.direcao === 'asc' ? 'desc' : 'asc')
                : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type='button'
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    const handleMovimentacao = (item) => {
        setProdutoMovSel(item);
        setShowMovModal(true);
    };

    return (
        <PageContainer>
            <h2 className='page-title'>Consulta de Estoque</h2>

            <PageHeader>
                <div className='group-div'>
                    <Input
                        label='Produto ID'
                        value={produtoId}
                        onChange={(e) => setProdutoId(e.target.value)}
                        size={120}
                    />
                    <Input
                        label='Descrição'
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                        size={260}
                    />
                    <div style={{ display: 'flex', alignItems: 'end', paddingBottom: '6px' }}>
                        <div className='form-check'>
                            <input
                                id='somenteAbaixoMin'
                                className='form-check-input'
                                type='checkbox'
                                checked={somenteAbaixoMin}
                                onChange={(e) => setSomenteAbaixoMin(e.target.checked)}
                            />
                            <label className='form-check-label' htmlFor='somenteAbaixoMin'>
                                Somente abaixo do mínimo
                            </label>
                        </div>
                    </div>
                </div>
                <button
                    style={{ marginRight: '5px' }}
                    type='button'
                    className='btn btn-search'
                    onClick={buscarEstoque}
                    disabled={disableBtn}
                >
                    Buscar
                </button>
            </PageHeader>

            <PageContent>
                <table className='page-content-table'>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('id', 'ID', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('produtoId', 'Produto ID', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('descricao', 'Descrição')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('quantidade', 'Quantidade', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('estoqueMin', 'Est. Mín.', { textAlign: 'right' })}
                            </th>
                            <th>{renderOrdenadorColuna('status', 'Status')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {renderOrdenadorColuna('preco', 'Preço', { textAlign: 'right' })}
                            </th>
                            <th style={{ textAlign: 'center' }}>
                                {renderOrdenadorColuna('dataUltMov', 'Últ. Mov. Data', { textAlign: 'center' })}
                            </th>
                            <th style={{ textAlign: 'center' }}>
                                {renderOrdenadorColuna('dataUltMov', 'Últ. Mov. Hora', { textAlign: 'center' })}
                            </th>
                            <th style={{ textAlign: 'right' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {estoquesOrdenados.map((item) => (
                            <tr key={item.id}>
                                <td style={{ textAlign: 'right' }}>{item.id}</td>
                                <td style={{ textAlign: 'right' }}>{item.produtoId}</td>
                                <td>{item.produto?.descricao || '-'}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumeric(item.quantidade)}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumeric(item.produto?.estoqueMin || 0)}</td>
                                <td>{getStatusEstoque(item)}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(item.produto?.preco)}</td>
                                <td style={{ textAlign: 'center' }}>{formatDate(item.dataUltMov)}</td>
                                <td style={{ textAlign: 'center' }}>{formatTime(item.dataUltMov)}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <button
                                        className='btn btn-sm btn-outline-primary'
                                        onClick={() => handleMovimentacao(item)}
                                    >
                                        Movimentação
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {estoquesFiltrados.length === 0 && (
                            <tr>
                                <td colSpan={10}>Nenhum registro encontrado.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </PageContent>

            <PageFooter>
                <div className='totais-footer'>
                    <strong>Registros: </strong> {estoquesFiltrados.length} | <strong>Qtd Total: </strong> {formatNumeric(totalQuantidade)}
                </div>
            </PageFooter>

            <ModalEstoqueMov
                show={showMovModal}
                onHide={() => setShowMovModal(false)}
                produto={produtoMovSel}
            />
        </PageContainer>
    );
};

export default PesqEstoquePage;
